import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '../../app/providers';
import { LocalDataTable } from '../../components/LocalDataTable';
import { api } from '../../lib/api';

type Line = {
  feesId: number;
  feesType: string | null;
  subject: string | null;
  grade: string | null;
  groupName: string | null;
  month: number | null;
  year: number | null;
  amount: number;
  paidAmount: number;
  remainingAmount: number;
};

type SubmissionView = {
  studentId: number;
  studentName: string;
  fatherName: string;
  lines: Line[];
  totalRemainingAmount: number;
};

const money = (value: number) =>
  value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Fee collection.
 *
 * Two modes, matching the two the client already has:
 *   Itemised - the cashier types an amount per fee line.
 *   Simple   - one lump sum, split pro rata across everything outstanding.
 *
 * The pro-rata split happens on the server in float arithmetic, exactly as it always has,
 * so the amounts written may not divide evenly. The UI does not attempt to predict or
 * pretty up the result: it shows what the server actually recorded.
 */
export function FeesSubmissionPage() {
  const toast = useToast();

  const [searchParams] = useSearchParams();

  const [studentId, setStudentId] = useState(() => searchParams.get('studentId') ?? '');
  const [view, setView] = useState<SubmissionView | null>(null);
  const [amounts, setAmounts] = useState<Record<number, string>>({});
  const [lumpSum, setLumpSum] = useState('');
  const [mode, setMode] = useState<'itemised' | 'simple'>('itemised');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function loadStudent(rawId: string = studentId) {
    const id = Number(rawId);
    if (!Number.isInteger(id) || id <= 0) {
      toast.error('Enter a valid student id.');
      return;
    }

    setLoading(true);
    try {
      const result = await api.get<SubmissionView>(`/api/studentfees/submission/${id}`);
      setView(result);
      setAmounts({});
      setLumpSum('');
    } catch (err) {
      setView(null);
      toast.error(err instanceof Error ? err.message : 'Could not load the student.');
    } finally {
      setLoading(false);
    }
  }

  /*
   * Arriving from Generate Advance with ?studentId=42 loads that student straight away, so
   * the operator lands on their outstanding lines rather than on an empty lookup box. The
   * legacy redirect carried the id the same way.
   *
   * The ref keeps it to one automatic load per id: loadStudent is recreated every render,
   * so an effect depending on it would loop.
   */
  const autoLoaded = useRef<string | null>(null);

  useEffect(() => {
    const fromUrl = searchParams.get('studentId');
    if (!fromUrl || autoLoaded.current === fromUrl) return;

    autoLoaded.current = fromUrl;
    setStudentId(fromUrl);
    void loadStudent(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const enteredTotal = Object.values(amounts).reduce((sum, value) => sum + (Number(value) || 0), 0);

  async function submitItemised() {
    if (!view) return;

    setSubmitting(true);
    try {
      // Amount and PaidAmount are echoed back because the server's validation compares
      // against them, exactly as the legacy form post did. Sending the values we were
      // given keeps behaviour identical rather than accidentally "fixing" it here.
      const { feesSlipId } = await api.post<{ feesSlipId: number }>('/api/studentfees/submit', {
        studentId: view.studentId,
        lines: view.lines.map((line) => ({
          feesId: line.feesId,
          amount: line.amount,
          paidAmount: line.paidAmount,
          submitAmount: Number(amounts[line.feesId]) || 0,
        })),
      });

      toast.success(`Fees submitted. Slip FS-${feesSlipId} created.`);
      await loadStudent();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  }

  async function submitSimple() {
    if (!view) return;

    setSubmitting(true);
    try {
      const { feesSlipId } = await api.post<{ feesSlipId: number }>('/api/studentfees/submit-simple', {
        studentId: view.studentId,
        totalSubmitAmount: Number(lumpSum) || 0,
        totalRemainingAmount: view.totalRemainingAmount,
      });

      toast.success(`Fees submitted. Slip FS-${feesSlipId} created.`);
      await loadStudent();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Submission failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="page__header">
        <div>
          <h2 className="page__title">Fees Submission</h2>
          <p className="page__subtitle">Collect fees against a student's outstanding lines.</p>
        </div>
      </div>

      {/* A form rather than a keydown handler: Enter to submit is what a form already does,
          and it keeps this consistent with the other search boxes. */}
      <form
        className="card card--sm"
        style={{ marginBottom: 24 }}
        onSubmit={(e) => { e.preventDefault(); void loadStudent(); }}
      >
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="field" style={{ margin: 0, minWidth: 200 }}>
            <label htmlFor="student-id">Student ID</label>
            <input
              id="student-id"
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
              placeholder="e.g. 42"
              inputMode="numeric"
            />
          </div>
          <button type="submit" className="btn btn--primary" disabled={loading}>
            {loading ? 'Loading…' : 'Find student'}
          </button>
        </div>
      </form>

      {view && (
        <div className="card">
          <div className="page__header">
            <div>
              <h3 className="card__title" style={{ marginBottom: 2 }}>
                {view.studentName} <span style={{ color: 'var(--text-muted)' }}>STD-{view.studentId}</span>
              </h3>
              <p className="page__subtitle">Father: {view.fatherName}</p>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                className={mode === 'itemised' ? 'btn btn--primary btn--sm' : 'btn btn--sm'}
                onClick={() => setMode('itemised')}
              >
                Itemised
              </button>
              <button
                type="button"
                className={mode === 'simple' ? 'btn btn--primary btn--sm' : 'btn btn--sm'}
                onClick={() => setMode('simple')}
              >
                Simple
              </button>
            </div>
          </div>

          {view.lines.length === 0 ? (
            <p style={{ color: 'var(--text-muted)' }}>Nothing outstanding for this student.</p>
          ) : (
            <>
              {/* Paged for display only. `amounts` is keyed by fee id and the submit posts
                  every line, so entries typed on one page survive moving to another. */}
              <LocalDataTable
                rows={view.lines}
                rowKey={(line) => line.feesId}
                emptyMessage="Nothing outstanding."
                columns={[
                  { key: 'type', header: 'Type', render: (line) => line.feesType ?? '—' },
                  {
                    key: 'course', header: 'Course',
                    render: (line) =>
                      [line.grade, line.subject, line.groupName].filter(Boolean).join(' · ') || '—',
                  },
                  {
                    key: 'period', header: 'Period',
                    render: (line) => (line.month && line.year ? `${line.month}/${line.year}` : '—'),
                  },
                  {
                    key: 'amount', header: 'Amount', align: 'right',
                    render: (line) => <span className="money">{money(line.amount)}</span>,
                  },
                  {
                    key: 'paid', header: 'Paid', align: 'right',
                    render: (line) => <span className="money">{money(line.paidAmount)}</span>,
                  },
                  {
                    key: 'remaining', header: 'Remaining', align: 'right',
                    render: (line) => <span className="money">{money(line.remainingAmount)}</span>,
                  },
                  ...(mode === 'itemised'
                    ? [{
                        key: 'submit',
                        header: 'Submit',
                        align: 'right' as const,
                        render: (line: Line) => (
                          <input
                            className="input"
                            style={{ maxWidth: 120, textAlign: 'right' }}
                            value={amounts[line.feesId] ?? ''}
                            onChange={(e) =>
                              setAmounts((current) => ({ ...current, [line.feesId]: e.target.value }))
                            }
                            inputMode="decimal"
                            placeholder="0"
                            aria-label={`Amount to submit for fee ${line.feesId}`}
                          />
                        ),
                      }]
                    : []),
                ]}
              />

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  gap: 16,
                  marginTop: 20,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ color: 'var(--text-muted)' }}>
                  Total outstanding:{' '}
                  <strong className="money" style={{ color: 'var(--text)' }}>
                    {money(view.totalRemainingAmount)}
                  </strong>
                </div>

                {mode === 'itemised' ? (
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <span style={{ color: 'var(--text-muted)' }}>
                      Entered: <strong className="money">{money(enteredTotal)}</strong>
                    </span>
                    <button
                      type="button"
                      className="btn btn--accent"
                      onClick={() => void submitItemised()}
                      disabled={submitting || enteredTotal <= 0}
                    >
                      {submitting ? 'Submitting…' : 'Submit fees'}
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                    <div className="field" style={{ margin: 0, minWidth: 180 }}>
                      <label htmlFor="lump-sum">Total amount received</label>
                      <input
                        id="lump-sum"
                        value={lumpSum}
                        onChange={(e) => setLumpSum(e.target.value)}
                        inputMode="decimal"
                        placeholder="0"
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn--accent"
                      onClick={() => void submitSimple()}
                      disabled={submitting || !(Number(lumpSum) > 0)}
                    >
                      {submitting ? 'Submitting…' : 'Submit fees'}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
